/**
 * Scroll-driven sections.
 *
 * These three are the reason the storefront reads the way it does, and they are
 * the only places where scroll position drives a timeline directly rather than
 * triggering one. All three pin: the section reserves more page height than it
 * occupies, and the extra height is the timeline.
 *
 * Timings and offsets are measured from the Athora reference storefront.
 */
import { animate, scroll, frame, resize, clamp } from '@theme/motion';
import { prefersReducedMotion } from '@theme/utilities';

const canAnimate = () => !prefersReducedMotion();
const withPageScroller = (options) => options;

/** Pointer-driven layouts are desktop-only; below this the sections lay out flat. */
const isDesktop = () => matchMedia('(hover: hover) and (pointer: fine)').matches && innerWidth > 1024;

/* ==================================================================
 * <nuvrya-scroll-media-text>
 *
 * The signature moment. The media starts as a small rounded rectangle at the
 * centre of the frame, scaled to half size, and opens outward to full bleed
 * while the copy rises into place.
 *
 * The four keyframe stops are not evenly spaced. The mask does all its work in
 * the first 40% of the pin; the copy waits until 50% and then rises through the
 * remainder. That gap is what stops the two reading as one animation.
 * ================================================================== */
const FULL_BLEED = 'inset(0 round 0px)';

class ScrollMediaText extends HTMLElement {
  #animations = [];
  #controller;
  #mediaQuery = matchMedia('(min-width: 990px)');
  #pin;
  #scroller;

  connectedCallback() {
    const media = this.querySelector('.nuvrya-scroll-media-text__media');
    if (!media || !canAnimate()) return;

    // Explicit hook first: the media wrapper can hold a <picture>, a
    // placeholder SVG or a video-tag wrapper depending on what the merchant
    // set, and a tag-name selector silently misses all three.
    const inner =
      media.querySelector('[data-scroll-media]') ||
      media.querySelector('img, video, svg') ||
      media.firstElementChild;
    const content = this.querySelector('.nuvrya-scroll-media-text__content');

    this.#pin = this.parentElement;

    // These are Athora's four measured physical scroll stops. Native WAAPI
    // interpolates clip-path and transforms with the same `ease-out` curve,
    // while we drive currentTime from Horizon's real scroll container. Motion's
    // offset observer is window-centric for this four-stop sequence and froze
    // in a `.page-wrapper`; driving the identical keyframes directly keeps the
    // storefront and the standalone verification harness on the same timeline.
    const timing = { duration: 1000, fill: 'both', easing: 'linear' };
    const stops = [0, 0.4, 0.5, 1];
    const frames = (values) =>
      values.map((value, index) => ({ ...value, offset: stops[index], easing: 'ease-out' }));

    this.#animations.push(
      media.animate(
        frames([
          { clipPath: 'inset(calc(50% - var(--media-radius)) calc(50% - var(--media-radius)) round var(--media-radius))' },
          { clipPath: FULL_BLEED },
          { clipPath: FULL_BLEED },
          { clipPath: FULL_BLEED },
        ]),
        timing
      )
    );

    if (inner) {
      this.#animations.push(
        inner.animate(
          frames([
            { transform: 'scale(0.5)' },
            { transform: 'scale(1)' },
            { transform: 'scale(1)' },
            { transform: 'scale(1)' },
          ]),
          timing
        )
      );
    }

    if (content) {
      this.#animations.push(
        content.animate(
          frames([
            { opacity: 0, transform: 'translateY(255px)' },
            { opacity: 0, transform: 'translateY(255px)' },
            { opacity: 1, transform: 'translateY(0px)' },
            { opacity: 1, transform: 'translateY(0px)' },
          ]),
          timing
        )
      );
    }

    for (const animation of this.#animations) {
      animation.pause();
      animation.currentTime = 0;
    }

    this.#bindScroller();
    this.#mediaQuery.addEventListener('change', this.#bindScroller);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#mediaQuery.removeEventListener('change', this.#bindScroller);
    for (const animation of this.#animations) animation.cancel();
    this.#animations = [];
  }

  #bindScroller = () => {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#scroller = window;
    this.#scroller.addEventListener('scroll', this.#update, { passive: true, signal });
    window.addEventListener('resize', this.#update, { passive: true, signal });
    this.#update();
  };

  #update = () => {
    if (!this.#pin || this.#animations.length === 0) return;

    const isWindow = this.#scroller === window;
    const scrollTop = isWindow ? window.scrollY : this.#scroller.scrollTop;
    const containerTop = isWindow ? 0 : this.#scroller.getBoundingClientRect().top;
    const pinTop = this.#pin.getBoundingClientRect().top - containerTop + scrollTop;
    const progress = clamp(0, 1, (scrollTop - pinTop) / Math.max(1, this.#pin.offsetHeight - window.innerHeight));
    const currentTime = progress * 1000;

    for (const animation of this.#animations) animation.currentTime = currentTime;
  };
}

/* ==================================================================
 * <nuvrya-hero-collections>
 *
 * A rail of tilted cards that travels sideways while the section heading stays
 * pinned. Desktop only: it needs a tall pin and a pointer, and on a phone the
 * same content is better served as a swipe carousel, which is what the markup
 * falls back to.
 * ================================================================== */
class HeroCollections extends HTMLElement {
  #controller;
  #pin;
  #scroller;
  #track;
  #cards = [];
  #distance = 0;
  #sign = -1;
  #maxTilt = 7;
  #header;
  #stopResize;

  connectedCallback() {
    this.#init();
    this.#stopResize = resize(this, () => {
      this.#teardown();
      frame.render(() => this.#init());
    });
  }

  disconnectedCallback() {
    this.#teardown();
    this.#stopResize?.();
  }

  #teardown() {
    this.#controller?.abort();
    this.#controller = undefined;
    this.removeAttribute('data-pinned');
    if (this.#track) this.#track.style.transform = '';
    if (this.#header) {
      this.#header.style.opacity = '';
      this.#header.style.transform = '';
    }
    for (const card of this.#cards) {
      card.style.removeProperty('--nuvrya-card-tilt');
    }
    this.#pin?.style.removeProperty('--nuvrya-rail-height');
    this.#pin = undefined;
    this.#scroller = undefined;
    this.#track = undefined;
    this.#header = undefined;
    this.#cards = [];
  }

  #init() {
    if (!canAnimate() || !isDesktop()) return;

    this.#track = this.querySelector('.nuvrya-tilted-track');
    this.#header = this.querySelector('.nuvrya-hero-collections__header');
    this.#cards = Array.from(this.querySelectorAll('.nuvrya-tilted-card'));
    if (!this.#track || this.#cards.length === 0) return;

    this.setAttribute('data-pinned', 'true');
    this.#pin = this.parentElement ?? this;
    this.#scroller = window;

    // The rail starts after the editorial copy. Measure the actually visible
    // lane rather than the full viewport, otherwise the last cards never enter
    // cleanly and the composition feels cramped at both ends.
    const railStart = this.#track.offsetLeft;
    const visibleLane = Math.max(1, this.clientWidth - railStart);
    const endInset = Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--rail-end-inset')
    ) || 80;
    this.#distance = Math.max(0, this.#track.scrollWidth - visibleLane + endInset);
    this.#pin.style.setProperty('--nuvrya-rail-height', `${window.innerHeight + this.#distance / 1.25}px`);
    this.#sign = document.documentElement.dir === 'rtl' ? 1 : -1;

    // Athora holds each incoming card at -7deg, then settles it to zero during
    // the first half of that card's share of the pinned journey. Once settled,
    // it stays square instead of tilting through zero as it exits.
    this.#maxTilt = 7;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#scroller.addEventListener('scroll', this.#update, { passive: true, signal });
    window.addEventListener('resize', this.#update, { passive: true, signal });
    this.#update();
  }

  #update = () => {
    if (!this.#pin || !this.#track || !this.#scroller) return;

    const isWindow = this.#scroller === window;
    const scrollTop = isWindow ? window.scrollY : this.#scroller.scrollTop;
    const containerTop = isWindow ? 0 : this.#scroller.getBoundingClientRect().top;
    const viewportHeight = isWindow ? window.innerHeight : this.#scroller.clientHeight;
    const pinTop = this.#pin.getBoundingClientRect().top - containerTop + scrollTop;
    const range = Math.max(1, this.#pin.offsetHeight - viewportHeight);
    const scrollDelta = scrollTop - pinTop;
    const progress = clamp(0, 1, scrollDelta / range);
    const travel = this.#sign * this.#distance * progress;

    this.#track.style.transform = `translate3d(${travel}px, -50%, 0)`;

    if (this.#header) {
      const exit = clamp(0, 1, scrollDelta / Math.max(320, this.clientWidth * .32));
      const horizontalShift = 17.154 + (30 - 17.154) * progress;
      this.#header.style.opacity = `${1 - exit}`;
      this.#header.style.transform = `translate3d(${horizontalShift}px, -50%, 0) scale(${1 - 0.18 * exit})`;
    }

    const itemSpan = 1 / this.#cards.length;
    const settleSpan = itemSpan / 2;
    this.#cards.forEach((card, index) => {
      const settle = clamp(0, 1, 1 - (progress - index * itemSpan) / settleSpan);
      card.style.setProperty('--nuvrya-card-tilt', `${this.#sign * settle * this.#maxTilt}deg`);
    });
  };
}

/* ==================================================================
 * <nuvrya-card-stack>
 *
 * Content cards travel horizontally across a fixed background while the section
 * is pinned. The background never moves; only the cards do, which is what keeps
 * the video behind them readable.
 * ================================================================== */
class CardStack extends HTMLElement {
  #controller;
  #pin;
  #scroller;
  #track;
  #distance = 0;
  #sign = -1;
  #stopResize;

  connectedCallback() {
    this.#init();
    this.#stopResize = resize(this, () => {
      this.#teardown();
      frame.render(() => this.#init());
    });
  }

  disconnectedCallback() {
    this.#teardown();
    this.#stopResize?.();
  }

  #teardown() {
    this.#controller?.abort();
    this.#controller = undefined;
    if (this.#track) this.#track.style.transform = '';
    this.#pin = undefined;
    this.#scroller = undefined;
    this.#track = undefined;
  }

  #init() {
    this.#track = this.querySelector('.nuvrya-card-stack__track');
    if (!this.#track) return;

    if (!canAnimate() || !matchMedia('(min-width: 768px)').matches) {
      this.#track.style.transform = '';
      return;
    }

    this.#distance = Math.max(0, this.#track.scrollWidth - this.clientWidth);
    if (this.#distance === 0) return;

    this.#pin = this.parentElement ?? this;
    this.#scroller = window;
    this.#sign = document.documentElement.dir === 'rtl' ? 1 : -1;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#scroller.addEventListener('scroll', this.#update, { passive: true, signal });
    window.addEventListener('resize', this.#update, { passive: true, signal });
    this.#update();
  }

  #update = () => {
    if (!this.#pin || !this.#track || !this.#scroller) return;

    const isWindow = this.#scroller === window;
    const scrollTop = isWindow ? window.scrollY : this.#scroller.scrollTop;
    const containerTop = isWindow ? 0 : this.#scroller.getBoundingClientRect().top;
    const viewportHeight = isWindow ? window.innerHeight : this.#scroller.clientHeight;
    const pinTop = this.#pin.getBoundingClientRect().top - containerTop + scrollTop;
    const range = Math.max(1, this.#pin.offsetHeight - viewportHeight);
    const progress = clamp(0, 1, (scrollTop - pinTop) / range);

    this.#track.style.transform = `translate3d(${this.#sign * this.#distance * progress}px, 0, 0)`;
  };
}

/* ==================================================================
 * <nuvrya-hero-stack-scroll>
 *
 * Stacked layers that scale and clip through one another. Used on the About
 * page as the opening statement.
 * ================================================================== */
class HeroStackScroll extends HTMLElement {
  #stops = [];

  connectedCallback() {
    if (!canAnimate()) return;

    const layers = Array.from(this.querySelectorAll('.nuvrya-hero-stack__layer'));
    if (layers.length === 0) return;

    layers.forEach((layer, index) => {
      // Later layers start further back and arrive later, so the stack resolves
      // front-to-back rather than all at once.
      const from = 1 + index * 0.08;
      const start = clamp(0, 0.6, index * 0.12);

      this.#stops.push(
        scroll(
          animate(
            layer,
            { scale: [from, 1], opacity: [0, 1, 1] },
            { ease: 'linear' }
          ),
          withPageScroller({ target: this.parentElement ?? this, offset: [`${start} start`, 'end end'] })
        )
      );
    });
  }

  disconnectedCallback() {
    for (const stop of this.#stops) stop?.();
    this.#stops = [];
  }
}

const REGISTRY = {
  'nuvrya-scroll-media-text': ScrollMediaText,
  'nuvrya-hero-collections': HeroCollections,
  'nuvrya-card-stack': CardStack,
  'nuvrya-hero-stack-scroll': HeroStackScroll,
};

for (const [tag, constructor] of Object.entries(REGISTRY)) {
  if (!customElements.get(tag)) customElements.define(tag, constructor);
}

export { ScrollMediaText, HeroCollections, CardStack, HeroStackScroll };
